import React, { useEffect, useState } from 'react';

import { Botao, Checkbox, Field, Input, Modal, Nota, Textarea } from '../../../ui';
import { PreviaExpurgo } from '../../../api/associado-expurgo/associadoExpurgoApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';

interface ModalConfirmarExpurgoProps {
  aberto: boolean;
  previa: PreviaExpurgo | null;
  processando?: boolean;
  aoConfirmar: (dados: { motivo: string; marcarTesteNoSga: boolean }) => void;
  aoCancelar: () => void;
}

export const MINIMO_MOTIVO = 5;

/** Só dígitos — o que o usuário digita pode vir com máscara, o CPF do banco também. */
const digitos = (valor: string) => (valor ?? '').replace(/\D/g, '');

/**
 * Confirmação da exclusão definitiva (at-77, item 64).
 *
 * ⚠️ **Não usa o `ModalConfirmacao`** — aquele é sim/não e não acomoda campo. Aqui
 * há **motivo obrigatório** (é a única explicação que sobra depois que o registro
 * some) e a **digitação do CPF**, que é a barreira contra o clique por engano numa
 * ação que não tem desfazer. Mesma decisão do `ModalDistrato` (at-40/at-69), que é
 * o molde deste.
 *
 * 🔴 **O checkbox do SGA não é enfeite.** Quando o associado já está cadastrado lá,
 * o backend **recusa** a exclusão sem ele: o SGA não tem rota de exclusão, e apagar
 * só aqui faria o cadastro **voltar** na próxima sincronização automática. Marcar
 * manda o SAAC gravar a situação TESTE DE SISTEMA no SGA antes de apagar — e, se
 * essa marcação falhar, nada é apagado.
 */
const ModalConfirmarExpurgo: React.FC<ModalConfirmarExpurgoProps> = ({
  aberto,
  previa,
  processando,
  aoConfirmar,
  aoCancelar,
}) => {
  const [motivo, setMotivo] = useState('');
  const [cpfDigitado, setCpfDigitado] = useState('');
  const [marcarNoSga, setMarcarNoSga] = useState(false);

  // Limpa ao reabrir: nada de um expurgo vazar para o seguinte.
  useEffect(() => {
    if (aberto) {
      setMotivo('');
      setCpfDigitado('');
      setMarcarNoSga(false);
    }
  }, [aberto]);

  if (!previa) return null;

  const cpfEsperado = digitos(previa.associado.cpfCnpj);
  const cpfConfere = digitos(cpfDigitado) === cpfEsperado && cpfEsperado.length > 0;
  const motivoValido = motivo.trim().length >= MINIMO_MOTIVO;
  const sgaOk = !previa.exigeMarcarNoSga || marcarNoSga;
  const podeConfirmar = motivoValido && cpfConfere && sgaOk;

  const erroMotivo =
    motivo.length > 0 && !motivoValido
      ? `Descreva o motivo com pelo menos ${MINIMO_MOTIVO} caracteres.`
      : '';
  const erroCpf =
    cpfDigitado.length > 0 && !cpfConfere
      ? 'O CPF digitado não confere com o do cadastro.'
      : '';

  const total = previa.inventario;

  return (
    <Modal
      aberto={aberto}
      titulo="Apagar este cadastro em definitivo?"
      descricao={`${previa.associado.nomeCompleto} — ${formatCpfCnpj(previa.associado.cpfCnpj)}`}
      tamanho="md"
      ocupado={processando}
      aoFechar={aoCancelar}
      alinharRodape="direita"
      rodape={
        <>
          <Botao variante="secundario" disabled={processando} onClick={aoCancelar}>
            Voltar
          </Botao>
          <Botao
            variante="perigo"
            carregando={processando}
            disabled={!podeConfirmar}
            onClick={() =>
              aoConfirmar({ motivo: motivo.trim(), marcarTesteNoSga: marcarNoSga })
            }
          >
            Apagar em definitivo
          </Botao>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Nota variante="atencao">
          <strong>Isto não tem como ser desfeito.</strong> Serão apagados{' '}
          {total.veiculos} veículo(s), {total.fotosDeDocumento + total.fotosDeVistoria}{' '}
          foto(s), {total.vistorias} vistoria(s), {total.contratos} contrato(s) e{' '}
          {total.arquivosNoStorage} arquivo(s) do armazenamento. O histórico de envios
          ao SGA é preservado.
        </Nota>

        {previa.exigeMarcarNoSga && (
          <Checkbox
            rotulo="Marcar como TESTE DE SISTEMA no SGA antes de apagar"
            descricao={
              'Obrigatório: este associado tem cadastro no SGA. O SGA não permite ' +
              'excluir, e apagar só aqui faria o registro voltar na próxima ' +
              'sincronização. Se o SGA não responder, nada será apagado.'
            }
            checked={marcarNoSga}
            disabled={processando}
            onChange={setMarcarNoSga}
          />
        )}

        <Field
          rotulo="Motivo"
          obrigatorio
          dica="Fica registrado no histórico de exclusões — é a única explicação que sobra depois que o cadastro some."
          erro={erroMotivo}
          campo="motivo"
        >
          <Textarea
            rows={3}
            value={motivo}
            disabled={processando}
            onChange={(evento) => setMotivo(evento.target.value)}
            placeholder="Ex.: cadastro de teste de câmera feito em produção."
          />
        </Field>

        <Field
          rotulo="Digite o CPF para confirmar"
          obrigatorio
          dica={`Confirme digitando ${formatCpfCnpj(previa.associado.cpfCnpj)}.`}
          erro={erroCpf}
          campo="cpf-confirmacao"
        >
          <Input
            value={cpfDigitado}
            inputMode="numeric"
            disabled={processando}
            onChange={(evento) => setCpfDigitado(evento.target.value)}
            placeholder="000.000.000-00"
          />
        </Field>
      </div>
    </Modal>
  );
};

export default ModalConfirmarExpurgo;
